<template>
  <tr :class="{ 'blue-grey darken-2': processed, 'elevation-3': editing }">
    <td colspan="4">
      <v-form ref="form">
        <v-row align="center" class="my-0">
          <v-col :cols="type === 'ua' ? 3 : 6" class="d-flex align-center pr-0">
            <slot name="select" :selectable="!processed && !loading && !editing"></slot>
            <span v-if="!editing" class="text-break">{{ model.pattern }}</span>
            <v-textarea
              v-else
              v-model="model.pattern"
              :rules="rules.pattern"
              :disabled="processed || loading"
              append-icon="mdi-restore"
              solo
              flat
              hide-details="auto"
              rows="1"
              auto-grow
              class="text-subtitle-2"
              data-cy="pattern-input"
              @click:append="resetPattern"
            />
            <div v-if="type === 'ua'" class="ml-auto d-flex align-center">
              <ai-button
                v-if="editing"
                :loading="aiLoading"
                :disabled="processed || loading"
                :can-view="canView()"
                :is-v2="isV2()"
                @generate="generatePatternWithMav"
                @generate-direct="generatePatternDirectUpgrade"
              />
              <v-btn :disabled="isDisabled" icon @click="editPattern">
                <v-icon>{{ editing ? 'mdi-close' : 'mdi-pencil' }}</v-icon>
              </v-btn>
            </div>
          </v-col>
          <v-col
            v-if="type === 'ua'"
            v-placeholder
            cols="2"
            class="d-flex justify-space-between align-center"
          >
            <span>{{ domain }}</span>
            <v-btn v-if="domain" icon @click="domain = ''">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-btn v-else-if="item.domain" icon @click="domain = item.domain">
              <v-icon>mdi-restore</v-icon>
            </v-btn>
          </v-col>
          <v-col :cols="type === 'ua' ? 3 : 4" :lg="type === 'ua' ? 2 : 4">
            <app-dict-search
              v-model="model.app_dictionary_id"
              :rules="rules.appId"
              :disabled="isDisabled"
              inline
            />
          </v-col>
          <v-col v-if="type === 'ua'" v-placeholder class="text-truncate" cols="1" lg="2">
            {{ item.app_name }}
          </v-col>
          <v-col v-if="type === 'ua'" v-placeholder class="text-truncate" cols="1">
            {{ item.countries.join(', ') | uppercase }}
          </v-col>
          <nv-show :header="model.effective_start_date ? 'Start Date' : ''" cols="1" no-placeholder>
            <owl-date-picker
              v-model="model.effective_start_date"
              :disabled="isDisabled"
              label="Start Date"
              icon
              clearable
            />
            <span v-if="model.effective_start_date" class="mr-1">
              {{ model.effective_start_date }}
            </span>
            <v-tooltip v-if="type === 'bundle' && canView()" bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="ai-button ml-2"
                  :loading="aiLoading"
                  :disabled="isDisabled"
                  v-bind="attrs"
                  v-on="on"
                  @click="predictBundleWithMav"
                >
                  <v-icon small color="white">mdi-robot</v-icon>
                </v-btn>
              </template>
              <span>Predict App with MAV</span>
            </v-tooltip>
          </nv-show>
          <v-col
            v-if="item.locked_by === $auth.user.email"
            cols="1"
            class="d-flex justify-end align-center"
          >
            <template v-if="onTime(item.lock_expires_at)">
              <v-btn
                v-if="!processed"
                ref="save-btn"
                :loading="loading"
                :disabled="isDisabled"
                color="secondary"
                small
                class="ml-4"
                @click="onSave"
              >
                {{ isWildCardPattern() ? 'Test' : 'Save' }}
                <template #loader>
                  <nv-circular-loader />
                </template>
              </v-btn>
              <done-chip v-else />
            </template>
            <expire-chip v-else />
          </v-col>
        </v-row>
        <v-row v-if="editing">
          <nv-show
            :cols="isAutoFormat(item.user_agent) ? 4 : 6"
            :sm="isAutoFormat(item.user_agent) ? 4 : 6"
            header="User Agent"
          >
            <span class="text-break">{{ item.user_agent }}</span>
          </nv-show>
          <v-col v-if="isAutoFormat(item.user_agent)" cols="2">
            <v-btn text outlined class="mt-1" @click="autoCreate(item.user_agent)">
              Auto Pattern
            </v-btn>
          </v-col>
          <wildcards-table />
        </v-row>
      </v-form>
      <test-pattern-dialog
        v-if="isWildCardPattern()"
        v-model="testPatternDialog.show"
        :domain="domain"
        :pattern="model.pattern"
        product="ios/ua"
        @saved="save"
      />
      <pattern-comparison-dialog
        v-model="patternComparisonDialog.show"
        :original-pattern="model.pattern"
        :ai-generated-pattern="aiGeneratedPattern"
        :confidence-score="confidenceScore"
        :domain="domain"
        product="ios/ua"
        @use-ai-pattern="useAiPatternAndSendFeedback"
        @keep-original="keepOriginalAndSendFeedback"
        @cancel="patternComparisonDialog.show = false"
      />
    </td>
  </tr>
</template>

<script>
import MAV from '@/api/mav';
import AppDictSearch from '@/components/mv/AppDictSearch';
import ClassifyRowMixin from '@/components/cls/ClassifyRowMixin';
import OwlDatePicker from '@/components/OwlDatePicker';
import Rules from 'bsm-validation-rules';
import TestPatternDialog from '@/components/cls/TestPatternDialog';
import WildcardsTable from '@/components/cls/ios/WildcardsTable';
import DoneChip from '@/components/cls/DoneChip.vue';
import ExpireChip from '@/components/cls/ExpireChip.vue';
import PatternComparisonDialog from '@/components/cls/PatternComparisonDialog.vue';
import AiButton from '@/components/cls/AIButton.vue';
import { getConfigMap } from '@/helpers/filters';
import { submitFeedback } from '@/api/feedback';

export const rules = {
  appId: [Rules.presence()],
  pattern: [
    Rules.presence(),
    Rules.format(/^[^/$]+\/[^$]*((\$[ADHN]\$)?[^$]?)*$/),
    Rules.format(/^[^\r\n]*$/, 'should not include a new line character'),
  ],
};

export function isAutoFormat(ua) {
  return /^[\w\-. ]+\/[\d.]+ CFNetwork\/[\d.]+ Darwin\/[\d.]+$/.test(ua);
}

export function isWildcardRulePattern(pattern) {
  return /\$[ADHN]\$/.test(pattern);
}

export default {
  components: {
    ExpireChip,
    DoneChip,
    WildcardsTable,
    TestPatternDialog,
    PatternComparisonDialog,
    'ai-button': AiButton,
    OwlDatePicker,
    AppDictSearch,
  },
  mixins: [ClassifyRowMixin],
  props: {
    type: {
      type: String,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const pattern = this.type === 'bundle' ? this.item.bundle_id : this.item.user_agent;
    return {
      model: {
        pattern,
        app_dictionary_id: null,
        effective_start_date: null,
      },
      formattedPayload: {},
      rules: {
        appId: [],
      },
      editing: false,
      testPatternDialog: { show: false },
      patternComparisonDialog: { show: false },
      domain: this.item.domain,
      countries: this.item.countries,
      aiLoading: false,
      aiGeneratedPattern: '',
      aiSuggestionFull: null,
      confidenceScore: 0.8,
      feedbackSent: false,
      bundleSuggestionFull: null,
    };
  },
  computed: {
    isDisabled() {
      return this.processed || this.loading || this.isSelected;
    },
  },
  methods: {
    save() {
      this.rules = rules;
      this.formattedPayload = this.model;
      if (this.domain) {
        this.model.domain = this.domain;
      }
      if (this.type === 'ua' && this.countries && !this.isWildCardPattern()) {
        this.model.country = this.countries[0];
      }

      // UA feedback
      if (
        this.type === 'ua' &&
        this.aiSuggestionFull &&
        this.model.pattern !== this.item.user_agent
      ) {
        if (this.isProductionMode() && !this.feedbackSent) {
          const action =
            this.aiSuggestionFull.pattern === this.model.pattern ? 'ACCEPTED' : 'CORRECTED';
          this.sendFeedback(action, this.model.pattern);
        }
      }

      // Bundle feedback
      if (this.type === 'bundle' && this.bundleSuggestionFull && this.model.app_dictionary_id) {
        if (this.isProductionMode() && !this.feedbackSent) {
          const action =
            this.bundleSuggestionFull.application_id === this.model.app_dictionary_id
              ? 'ACCEPTED'
              : 'CORRECTED';
          this.sendBundleFeedback(action, this.model.app_dictionary_id);
        }
      }

      this.feedbackSent = false;

      // iOS shadow evaluation (app-level shadow)
      try {
        if (this.isAppShadowMode()) {
          if (this.type === 'ua') {
            this.sendIosUaShadowEvaluation();
          } else if (this.type === 'bundle') {
            this.sendIosBundleShadowEvaluation();
          }
        }
      } catch (error) {
        console.log(error);
      }

      this.submitClassify(`/ios/${this.type}/discoveries/${this.item.id}/rules`, {
        success: () => {
          this.editing = false;
          this.$emit('expand', false);
          this.$emit('saved');
        },
      });
    },
    editPattern() {
      this.editing = !this.editing;
      if (!this.processed && !this.loading) {
        this.resetPattern();
      }
    },
    resetPattern() {
      this.model.pattern = this.item.user_agent;
    },
    onSave() {
      if (this.isWildCardPattern()) {
        this.testPatternDialog.show = true;
      } else {
        this.save();
      }
    },
    autoCreate(ua) {
      this.model.pattern = ua.replace(
        /\/[\d.]+ CFNetwork\/[\d.]+ Darwin\/[\d.]+$/,
        '/$D$ CFNetwork/$D$ Darwin/$D$',
      );
    },
    isAutoFormat,
    isWildCardPattern() {
      return this.type === 'ua' && isWildcardRulePattern(this.model.pattern);
    },
    getConfigMap,
    async sendFeedback(action, finalPattern) {
      try {
        await submitFeedback({
          input_string: this.item.user_agent,
          ai_suggestion: {
            pattern: this.aiSuggestionFull?.pattern || this.aiGeneratedPattern,
            rationale: this.aiSuggestionFull?.rationale || '',
            confidence: this.aiSuggestionFull?.confidence || this.confidenceScore,
            entity_type: 'ios',
          },
          user_final_decision: {
            pattern: finalPattern,
            entity_type: 'ios',
          },
          action,
        });
      } catch (error) {
        console.error('Failed to submit feedback:', error);
      }
    },
    async generatePatternWithMav() {
      this.aiLoading = true;
      try {
        const response = await this.callMavAPI(this.item.user_agent);
        this.aiGeneratedPattern = response.pattern;
        this.aiSuggestionFull = response;
        this.model.pattern = response.pattern;
      } catch (error) {
        console.error('AI pattern generation failed:', error);
        this.$flash('error', 'Failed to generate pattern with MAV AI');
      } finally {
        this.aiLoading = false;
      }
    },
    async generatePatternDirectUpgrade() {
      this.aiLoading = true;
      try {
        const response = await this.callMavAPI(this.item.user_agent);
        this.aiGeneratedPattern = response.pattern;
        this.aiSuggestionFull = response;
        this.confidenceScore = response.confidence || 0;
        this.patternComparisonDialog.show = true;
      } catch (error) {
        console.error('AI pattern generation failed:', error);
        this.$flash('error', 'Failed to generate pattern with MAV AI');
      } finally {
        this.aiLoading = false;
      }
    },
    useAiPatternAndSendFeedback() {
      this.model.pattern = this.aiGeneratedPattern;
      this.sendFeedback('ACCEPTED', this.aiGeneratedPattern);
      this.feedbackSent = true;
      this.patternComparisonDialog.show = false;
    },
    keepOriginalAndSendFeedback() {
      this.sendFeedback('CORRECTED', this.model.pattern);
      this.feedbackSent = true;
      this.patternComparisonDialog.show = false;
    },
    async callMavAPI(user_agent) {
      try {
        const response = await MAV.post('/patterns/ios/generate', {
          sample: user_agent,
        });
        return response.data;
      } catch (error) {
        console.error('MAV API error:', error);
        throw error;
      }
    },
    async predictBundleWithMav() {
      this.aiLoading = true;
      try {
        const response = await this.callMavBundleAPI();
        this.bundleSuggestionFull = response;
        if (response.application_id) {
          this.model.app_dictionary_id = response.application_id;
        }
      } catch (error) {
        console.error('AI bundle prediction failed:', error);
        this.$flash('error', 'Failed to predict bundle with MAV AI');
      } finally {
        this.aiLoading = false;
      }
    },
    async callMavBundleAPI() {
      try {
        const payload = {
          bundle_id: this.item.bundle_id,
          app_name: this.item.app_name || '',
          user_agent: this.item.user_agent || '',
          countries: this.item.countries || [],
        };

        const response = await MAV.post('/predict/ios/bundle', payload);
        const prediction = response.data.prediction || response.data;

        return {
          application_id: prediction.application_id,
          application_name: prediction.application_name,
          is_cdn: prediction.is_cdn,
          match_confidence: prediction.match_confidence,
          match_source: prediction.match_source,
          reasoning_trace: prediction.reasoning_trace,
          entity_type: 'ios_bundle',
        };
      } catch (error) {
        console.error('MAV Bundle API error:', error);
        throw error;
      }
    },
    async sendBundleFeedback(action, finalAppId) {
      try {
        await submitFeedback({
          input_string: this.item.bundle_id,
          ai_suggestion: {
            application_id: this.bundleSuggestionFull?.application_id,
            rationale: this.bundleSuggestionFull?.reasoning_trace?.join(' | ') || '',
            confidence: this.bundleSuggestionFull?.match_confidence || 0,
            entity_type: 'ios_bundle',
          },
          user_final_decision: {
            application_id: finalAppId,
            entity_type: 'ios_bundle',
          },
          action,
        });
      } catch (error) {
        console.error('Failed to submit bundle feedback:', error);
      }
    },
    canView() {
      const config = this.getConfigMap();
      const mavConfig = config['mavFeature_owl_ui'];
      return mavConfig?.app_enabled?.v1 || false;
    },
    isV2() {
      const config = this.getConfigMap();
      return config['mavFeature_owl_ui']?.app_enabled?.v1 || false;
    },
    isAppShadowMode() {
      const config = this.getConfigMap();
      const appConfig = config['mavFeature_owl_ui']?.app_enabled;
      return appConfig?.shadow === true && !appConfig?.v1;
    },
    async sendIosUaShadowEvaluation() {
      try {
        await MAV.post('/shadow/app/ios/ua', {
          domain: this.item.domain || '',
          user_agent: this.item.user_agent || '',
          app_name: this.item.app_name || '',
          expected_application_id: this.model.app_dictionary_id || null,
          countries: this.item.countries || [],
        });
      } catch (error) {
        console.error('iOS UA Shadow API error:', error);
      }
    },
    async sendIosBundleShadowEvaluation() {
      try {
        await MAV.post('/shadow/app/ios/ua', {
          domain: this.item.domain || '',
          user_agent: this.item.user_agent || '',
          app_name: this.item.app_name || '',
          expected_application_id: this.model.app_dictionary_id || null,
          countries: this.item.countries || [],
        });
      } catch (error) {
        console.error('iOS Bundle Shadow API error:', error);
      }
    },
    isProductionMode() {
      const config = this.getConfigMap();
      const mavConfig = config['mavFeature_owl_ui'];
      return (mavConfig?.enabled?.v1 || mavConfig?.enabled?.v2) && !mavConfig?.enabled?.shadow;
    },
  },
};
</script>

<style scoped lang="scss">
.theme--dark.v-data-table tbody tr.elevation-3 {
  &:hover {
    background-color: transparent !important;
  }

  .row:first-child {
    border-bottom: thin solid rgba(255, 255, 255, 0.12);
  }
}

.ai-button {
  left: 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3) !important;
  transition: all 0.3s ease !important;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important;
    box-shadow: 0 6px 12px rgba(102, 126, 234, 0.5) !important;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
  }
}
</style>
